import { useId, useState } from 'react';
import { fetchByUrl, PhotoError } from './photos.js';

/**
 * Where a photo comes from: upload, camera, or a web address.
 *
 * Shared by the capture form and the photo panel on an item, because they had
 * drifted. The panel grew tabs and a third source; the capture form kept two
 * stacked file inputs, so the same job looked like two different features
 * depending on whether the item existed yet. One component is what stops that
 * happening again.
 *
 * It hands back blobs and nothing else. What a photo *becomes* differs between
 * the two callers -- the panel writes an event immediately, the capture form
 * holds the file until the item it belongs to has been created -- and that is
 * the caller's business. This decides only where the bytes come from.
 */
export type PhotoSource = 'upload' | 'camera' | 'url';

/**
 * In the order offered.
 *
 * Upload leads because it is the one that always works: the camera needs a
 * device that has one, and a web address needs the service to be reachable.
 */
const SOURCES: { id: PhotoSource; label: string }[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'camera', label: 'Camera' },
  { id: 'url', label: 'URL' },
];

export function PhotoSourcePicker({
  disabled = false,
  onChoose,
  onError,
}: {
  disabled?: boolean;
  /** Called with what the user chose. Files from a picker, one blob from a URL. */
  onChoose: (photos: Blob[]) => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [source, setSource] = useState<PhotoSource>('upload');
  const [url, setUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const ids = { label: useId(), tab: useId(), panel: useId(), upload: useId(), camera: useId() };

  const chooseFiles = (input: HTMLInputElement) => {
    const files = [...(input.files ?? [])];
    // Cleared before the handler runs, so choosing the same file twice in a row
    // still fires a change event.
    input.value = '';
    if (files.length > 0) void onChoose(files);
  };

  async function fetchFromUrl() {
    const address = url.trim();
    if (address === '') return;

    setFetching(true);
    try {
      await onChoose([await fetchByUrl(address)]);
      setUrl('');
    } catch (caught) {
      onError(caught instanceof PhotoError ? caught.message : 'That image could not be fetched.');
    } finally {
      setFetching(false);
    }
  }

  return (
    <div className="field">
      <span className="field-label" id={ids.label}>
        Add a photo
      </span>

      <div className="segmented" role="tablist" aria-labelledby={ids.label}>
        {SOURCES.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            id={`${ids.tab}-${option.id}`}
            aria-selected={source === option.id}
            aria-controls={`${ids.panel}-${option.id}`}
            onClick={() => setSource(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`${ids.panel}-${source}`}
        aria-labelledby={`${ids.tab}-${source}`}
        className="photo-source"
      >
        {source === 'upload' && (
          <>
            <label htmlFor={ids.upload}>Choose from this device</label>
            <input
              id={ids.upload}
              type="file"
              accept="image/*"
              multiple
              disabled={disabled}
              onChange={(e) => chooseFiles(e.target)}
            />
          </>
        )}

        {source === 'camera' && (
          <>
            {/*
              `capture="environment"` asks for the rear camera directly, which is
              what FR-2.1 describes and what a plain file input does not do: on a
              phone that opens a chooser with the camera as one option among many.
              Desktop browsers ignore the attribute, hence the note.
            */}
            <label htmlFor={ids.camera}>Take a photo</label>
            <input
              id={ids.camera}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              disabled={disabled}
              onChange={(e) => chooseFiles(e.target)}
            />
            <p className="muted hint">
              Opens the camera on a phone. On a computer it asks for a file, since desktop browsers
              ignore the request.
            </p>
          </>
        )}

        {source === 'url' && (
          <>
            <label htmlFor={`${ids.panel}-address`}>Web address of an image</label>
            <div className="field inline">
              <input
                id={`${ids.panel}-address`}
                type="url"
                inputMode="url"
                placeholder="https://..."
                value={url}
                disabled={disabled}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  // Enter fetches rather than submitting whatever form this sits
                  // inside -- which, in the capture form, would save the item.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void fetchFromUrl();
                  }
                }}
              />
              <button
                type="button"
                className="primary"
                disabled={disabled || fetching || url.trim() === ''}
                onClick={() => void fetchFromUrl()}
              >
                {fetching ? 'Fetching…' : 'Fetch'}
              </button>
            </div>
            <p className="muted hint">
              The image is downloaded and kept with the item, so it stays if the page it came from
              changes.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
